/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// react and redux
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
// actions
import { addInstance, updateInstance, deleteInstance } from "../../../actions";
// forms
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
// BASE Components
import _ from "../../../../@lodash";
import BASELoading from "../../../../@BASE/core/BASELoading";
// material-ui components
import Box from "@mui/system/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
// custom components
import DeleteModal from "../../../shared-components/crud/DeleteModal";
import FormSectionHeader from "../../../shared-components/crud/instance/FormSectionHeader";

// form validation
const schema = yup.object().shape({
  name: yup.string().required("You must enter a name"),
  logo: yup.string().required("You must enter a logo"),
  address: yup.object().shape({
    street: yup.string().required("You must enter a street address"),
    city: yup.string().required("You must enter a city"),
    state: yup.string().required("You must select a state"),
    zip: yup.string().required("You must enter a ZIP code"),
  }),
});

const OrganizationForm = (props) => {
  const { instance, handleInstanceAction, loading, setOnSave, setSaveDisabled } = useOutletContext();

  // dispatch
  const dispatch = useDispatch();
  // route params
  const routeParams = useParams();
  // navigate
  const navigate = useNavigate();
  // states for address
  const states = [
    { label: "Alabama", value: "AL" },
    { label: "Alaska", value: "AK" },
    { label: "Arizona", value: "AZ" },
    { label: "Arkansas", value: "AR" },
    { label: "California", value: "CA" },
    { label: "Colorado", value: "CO" },
    { label: "Connecticut", value: "CT" },
    { label: "Delaware", value: "DE" },
    { label: "Florida", value: "FL" },
    { label: "Georgia", value: "GA" },
    { label: "Hawaii", value: "HI" },
    { label: "Idaho", value: "ID" },
    { label: "Illinois", value: "IL" },
    { label: "Indiana", value: "IN" },
    { label: "Iowa", value: "IA" },
    { label: "Kansas", value: "KS" },
    { label: "Kentucky", value: "KY" },
    { label: "Louisiana", value: "LA" },
    { label: "Maine", value: "ME" },
    { label: "Maryland", value: "MD" },
    { label: "Massachusetts", value: "MA" },
    { label: "Michigan", value: "MI" },
    { label: "Minnesota", value: "MN" },
    { label: "Mississippi", value: "MS" },
    { label: "Missouri", value: "MO" },
    { label: "Montana", value: "MT" },
    { label: "Nebraska", value: "NE" },
    { label: "Nevada", value: "NV" },
    { label: "New Hampshire", value: "NH" },
    { label: "New Jersey", value: "NJ" },
    { label: "New Mexico", value: "NM" },
    { label: "New York", value: "NY" },
    { label: "North Carolina", value: "NC" },
    { label: "North Dakota", value: "ND" },
    { label: "Ohio", value: "OH" },
    { label: "Oklahoma", value: "OK" },
    { label: "Oregon", value: "OR" },
    { label: "Pennsylvania", value: "PA" },
    { label: "Rhode Island", value: "RI" },
    { label: "South Carolina", value: "SC" },
    { label: "South Dakota", value: "SD" },
    { label: "Tennessee", value: "TN" },
    { label: "Texas", value: "TX" },
    { label: "Utah", value: "UT" },
    { label: "Vermont", value: "VT" },
    { label: "Virginia", value: "VA" },
    { label: "Washington", value: "WA" },
    { label: "West Virginia", value: "WV" },
    { label: "Wisconsin", value: "WI" },
    { label: "Wyoming", value: "WY" }
  ];
  // form
  const { control, watch, reset, handleSubmit, formState, getData, setValue } =
    useForm({
      mode: "onChange",
      resolver: yupResolver(schema),
    });
  // form state
  const { isValid, dirtyFields, errors } = formState;
  // watch form
  const form = watch();
  // Update the parent component with the form's validity and dirty state
  useEffect(() => {
    setSaveDisabled(_.isEmpty(dirtyFields) || !isValid);
  }, [formState]);
  // on form change set form state
  function onSubmit(data) {
    // dispatch add or update course
    if (routeParams.id === 'new') {
      handleInstanceAction(addInstance('organizations')(data), 'post');
    } else {
      handleInstanceAction(updateInstance('organizations')(data), 'patch');
    }
  }
  // Pass the handleSubmit function to setOnSave
  useEffect(() => {
    setOnSave(() => handleSubmit(onSubmit));
  }, []);
  // on user change reset form
  useEffect(() => {
    reset({ ...instance });
  }, [instance, reset]);
  // if no user return loading
  if (_.isEmpty(form) || !instance) {
    return <BASELoading />;
  }

  return (
    <div className="relative flex flex-col flex-auto">
      <div className="relative my-64 px-24 sm:px-32 max-w-4xl">
        <FormSectionHeader title="Basic Information" description="Enter the basic organization details below." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-4">
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  placeholder="Name"
                  id="name"
                  error={!!errors.name}
                  helperText={errors?.name?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-4">
            <Controller
              control={control}
              name="logo"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Logo"
                  placeholder="Logo"
                  id="orgLogo"
                  error={!!errors.logo}
                  helperText={errors?.logo?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
        </div>

        <Divider className="my-40" />

        <FormSectionHeader title="Address" description="Enter the organization address below." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="address.street"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Street Address"
                  placeholder="Street Address"
                  id="street"
                  error={!!errors.address?.street}
                  helperText={errors?.address?.street?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="address.city"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="City"
                  placeholder="City"
                  id="city"
                  error={!!errors.address?.city}
                  helperText={errors?.address?.city?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors.address?.state}
              required
            >
              <InputLabel id="state">State</InputLabel>
              <Controller
                control={control}
                name="address.state"
                defaultValue=''
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value || ''}
                    onChange={onChange}
                    label="State"
                    placeholder="State"
                    id="state"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a state</MenuItem>
                    {states.map((state) => (
                      <MenuItem key={state.value} value={state.value}>
                        {state.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="address.zip"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="ZIP Code"
                  placeholder="ZIP Code"
                  id="zip"
                  error={!!errors.address?.zip}
                  helperText={errors?.address?.zip?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
        </div>
      </div>

      <Box
        className="flex items-center mt-40 py-14 pr-16 pl-4 sm:pr-48 sm:pl-36 border-t"
        sx={{ backgroundColor: 'background.default' }}
      >
        {routeParams.id !== 'new' && (
          <Button
            variant="contained"
            color="error"
            // onClick={handleRemoveOrganization}
          >
            Delete
          </Button>
        )}
        <Button
          component={Link}
          className="ml-auto"
          to="/organizations"
        >
          Cancel
        </Button>
        <Button
          className="ml-8"
          variant="contained"
          color="secondary"
          disabled={_.isEmpty(dirtyFields) || !isValid}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </Box>
    </div> 
  );
};

export default OrganizationForm;
